// Trang cấu hình công thức vòng cược (Audit) cho từng mã khuyến mãi — chỉ admin
import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, Select, Switch, Space, Tag, Popconfirm, message, Alert, Divider,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined, CalculatorOutlined } from '@ant-design/icons';
import api, { errorMessage } from '../api';

const FORMULA_OPTIONS = [
  { value: 'turnover', label: '(Nạp + Thưởng) × Hệ số − Nạp' },
  { value: 'bonus', label: 'Thưởng × Hệ số' },
  { value: 'fixed', label: 'Vòng cược cố định' },
];

const FORMULA_TAG = {
  turnover: { color: 'blue', text: 'Nạp + Thưởng' },
  bonus: { color: 'green', text: 'Theo thưởng' },
  fixed: { color: 'default', text: 'Cố định' },
};

const EMPTY_RULE = {
  promo_code: '',
  formula: 'turnover',
  multiplier: 3,
  max_deposit: null,
  fixed_amount: null,
  tiers: [],
  manual_points: false,
  description: '',
};

const formatTier = (t) => `${t.min}${t.max === null || t.max === undefined ? '+' : t.max === t.min ? '' : ` – ${t.max}`}: ×${t.multiplier}`;

function PromoRules() {
  const [rules, setRules] = useState([]);
  const [defaultAudit, setDefaultAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = đóng, {} = thêm mới, rule = sửa
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState({ deposit: 100, bonus: 100, result: null, loading: false });
  const [form] = Form.useForm();
  const formula = Form.useWatch('formula', form);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await api.get('/promo-rules');
      setRules(res.data?.data || []);
      setDefaultAudit(res.data?.default_audit_amount ?? null);
    } catch (err) {
      message.error(errorMessage(err, 'Không tải được cấu hình khuyến mãi.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const openEditor = (rule) => {
    setEditing(rule || {});
    setPreview((p) => ({ ...p, result: null }));
    form.setFieldsValue(rule ? { ...EMPTY_RULE, ...rule, tiers: rule.tiers || [] } : EMPTY_RULE);
  };

  const closeEditor = () => {
    setEditing(null);
    form.resetFields();
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/promo-rules/${editing.id}`, values);
        message.success('Đã cập nhật cấu hình.');
      } else {
        await api.post('/promo-rules', values);
        message.success('Đã thêm cấu hình.');
      }
      closeEditor();
      fetchRules();
    } catch (err) {
      message.error(errorMessage(err, 'Lưu cấu hình thất bại.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule) => {
    try {
      await api.delete(`/promo-rules/${rule.id}`);
      message.success(`Đã xóa cấu hình ${rule.promo_code}.`);
      fetchRules();
    } catch (err) {
      message.error(errorMessage(err, 'Xóa thất bại.'));
    }
  };

  const handlePreview = async () => {
    setPreview((p) => ({ ...p, loading: true }));
    try {
      const res = await api.post('/promo-rules/preview', {
        rule: form.getFieldsValue(true),
        deposit_amount: preview.deposit,
        bonus_amount: preview.bonus,
      });
      setPreview((p) => ({ ...p, result: res.data?.data?.audit_amount, loading: false }));
    } catch (err) {
      message.error(errorMessage(err, 'Không tính được.'));
      setPreview((p) => ({ ...p, result: null, loading: false }));
    }
  };

  const columns = [
    { title: 'Mã KM', dataIndex: 'promo_code', key: 'promo_code', width: 110, render: (v) => <strong>{v}</strong> },
    {
      title: 'Kiểu',
      dataIndex: 'formula',
      key: 'formula',
      width: 130,
      render: (v) => <Tag color={FORMULA_TAG[v]?.color}>{FORMULA_TAG[v]?.text || v}</Tag>,
    },
    { title: 'Công thức', dataIndex: 'formula_text', key: 'formula_text' },
    {
      title: 'Hệ số / bậc',
      key: 'multiplier',
      render: (_, r) => {
        if (r.formula === 'fixed') return `Cố định ${r.fixed_amount}`;
        return (
          <div>
            <div>Mặc định ×{r.multiplier}</div>
            {r.tiers?.map((t, i) => (
              <Tag key={i} style={{ marginTop: 4 }}>{formatTier(t)}</Tag>
            ))}
          </div>
        );
      },
    },
    { title: 'Nạp tối đa', dataIndex: 'max_deposit', key: 'max_deposit', width: 100, render: (v) => (v === null ? '-' : v) },
    {
      title: 'Nhập điểm tay',
      dataIndex: 'manual_points',
      key: 'manual_points',
      width: 110,
      render: (v) => (v ? <Tag color="orange">Có</Tag> : '-'),
    },
    { title: 'Ghi chú', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: 'Sửa bởi', dataIndex: 'updated_by', key: 'updated_by', width: 100 },
    {
      title: 'Thao tác',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditor(r)}>Sửa</Button>
          <Popconfirm
            title={`Xóa cấu hình ${r.promo_code}?`}
            description={`Đơn mã này sẽ dùng vòng cược mặc định (${defaultAudit}).`}
            onConfirm={() => handleDelete(r)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filtered = rules.filter((r) => r.promo_code.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <Card
      title={<Space><CalculatorOutlined />Cấu hình khuyến mãi (vòng cược)</Space>}
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>Thêm mã khuyến mãi</Button>}
      style={{ background: '#FFF5EE' }}
    >
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message={`Khi duyệt đơn, hệ thống tính vòng cược theo cấu hình của mã. Mã chưa cấu hình sẽ dùng vòng cược mặc định: ${defaultAudit ?? '...'}.`}
      />
      <Input.Search
        placeholder="Tìm mã khuyến mãi..."
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ width: 280, marginBottom: 16 }}
      />
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        loading={loading}
        size="small"
        pagination={false}
        scroll={{ x: 1100 }}
      />

      <Modal
        open={editing !== null}
        title={editing?.id ? `Sửa cấu hình ${editing.promo_code}` : 'Thêm mã khuyến mãi'}
        onOk={handleSave}
        onCancel={closeEditor}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={saving}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={EMPTY_RULE}>
          <Form.Item
            name="promo_code"
            label="Mã khuyến mãi"
            normalize={(v) => (v || '').toUpperCase().trim()}
            rules={[
              { required: true, message: 'Nhập mã khuyến mãi' },
              { pattern: /^[A-Z0-9_-]{1,50}$/, message: 'Chỉ gồm chữ, số, "_" hoặc "-"' },
            ]}
          >
            <Input placeholder="VD: GK06" />
          </Form.Item>

          <Form.Item name="formula" label="Cách tính vòng cược" rules={[{ required: true }]}>
            <Select options={FORMULA_OPTIONS} />
          </Form.Item>

          {formula === 'fixed' ? (
            <Form.Item name="fixed_amount" label="Số vòng cược cố định" rules={[{ required: true, message: 'Nhập số vòng' }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          ) : (
            <>
              <Space size="large" style={{ display: 'flex' }}>
                <Form.Item
                  name="multiplier"
                  label="Hệ số (số vòng)"
                  rules={[{ required: true, message: 'Nhập hệ số' }]}
                  tooltip="Dùng khi tiền thưởng không rơi vào bậc nào bên dưới"
                >
                  <InputNumber min={0.01} max={1000} step={1} style={{ width: 160 }} />
                </Form.Item>
                {formula === 'turnover' && (
                  <Form.Item name="max_deposit" label="Tiền nạp tối đa được tính" tooltip="Để trống = không giới hạn">
                    <InputNumber min={0} style={{ width: 200 }} placeholder="Không giới hạn" />
                  </Form.Item>
                )}
              </Space>

              <Divider orientation="left" plain style={{ margin: '4px 0 12px' }}>
                Hệ số theo bậc tiền thưởng (không bắt buộc)
              </Divider>
              <Form.List name="tiers">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...rest }) => (
                      <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 4 }}>
                        <Form.Item {...rest} name={[name, 'min']} rules={[{ required: true, message: 'Nhập' }]}>
                          <InputNumber min={0} placeholder="Thưởng từ" style={{ width: 130 }} />
                        </Form.Item>
                        <Form.Item {...rest} name={[name, 'max']}>
                          <InputNumber min={0} placeholder="đến (trống = ∞)" style={{ width: 150 }} />
                        </Form.Item>
                        <Form.Item {...rest} name={[name, 'multiplier']} rules={[{ required: true, message: 'Nhập' }]}>
                          <InputNumber min={0.01} placeholder="Hệ số" style={{ width: 100 }} />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small">
                      Thêm bậc
                    </Button>
                  </>
                )}
              </Form.List>
            </>
          )}

          <Form.Item
            name="manual_points"
            label="Nhập số điểm thủ công khi duyệt"
            valuePropName="checked"
            tooltip="Bật: người duyệt phải tự nhập số điểm cộng; mã này sẽ không được duyệt tự động"
            style={{ marginTop: 16 }}
          >
            <Switch />
          </Form.Item>

          <Form.Item name="description" label="Ghi chú">
            <Input maxLength={255} />
          </Form.Item>
        </Form>

        <Card size="small" title="Tính thử" style={{ background: '#fafafa' }}>
          <Space wrap>
            <span>Nạp</span>
            <InputNumber min={0} value={preview.deposit} onChange={(v) => setPreview((p) => ({ ...p, deposit: v }))} />
            <span>Thưởng</span>
            <InputNumber min={0} value={preview.bonus} onChange={(v) => setPreview((p) => ({ ...p, bonus: v }))} />
            <Button onClick={handlePreview} loading={preview.loading}>Tính</Button>
            {preview.result !== null && preview.result !== undefined && (
              <span>Vòng cược: <strong style={{ color: '#1890ff' }}>{Number(preview.result).toLocaleString()}</strong></span>
            )}
          </Space>
        </Card>
      </Modal>
    </Card>
  );
}

export default PromoRules;
